import { Listeners } from '~js/components/listeners';
import * as s from '~js/utils/selectors';

/**
 * Constructor class that creates a new instance of a product form controller.
 *
 * @param {Element} element - DOM element which is equal to the <form> node wrapping product form inputs
 * @param {Object} product - A product object
 * @param {Object} options - Optional options object
 * @param {Function} options.onOptionChange - Callback for whenever an option input changes
 * @param {Function} options.onPlanChange - Callback for changes to name=selling_plan
 * @param {Function} options.onQuantityChange - Callback for whenever an quantity input changes
 * @param {Function} options.onPropertyChange - Callback for whenever a property input changes
 * @param {Function} options.onFormSubmit - Callback for whenever the product form is submitted
 */
export class ProductForm {
  constructor(element, product, options) {
    this.element = element;
    this.form = this.element.tagName == 'FORM' ? this.element : this.element.querySelector('form');
    this.product = this._validateProductObject(product);
    this.variantElement = this.element.querySelector(s.selectors$Y.idInput);

    options = options || {};

    this._listeners = new Listeners();
    this._listeners.add(this.element, 'submit', this._onSubmit.bind(this, options));

    this.optionInputs = this._initInputs(s.selectors$Y.optionInput, options.onOptionChange);

    this.planInputs = this._initInputs(s.selectors$Y.planInput, options.onPlanChange);

    this.quantityInputs = this._initInputs(s.selectors$Y.quantityInput, options.onQuantityChange);

    this.propertyInputs = this._initInputs(s.selectors$Y.propertyInput, options.onPropertyChange);

    const fixYotpoSubscription = true; // Set this to true only if Yotpo Subscriptions are enabled on PDP and they have issue with AddToCartForm targetting.

    if (fixYotpoSubscription && this.isSubscriptionProduct()) {

      this.productDetailsElement = document.querySelector('.product-single__details');
      this.productFormElement = this.productDetailsElement?.querySelector('.product__block.product__form__wrapper form');
      
      if(this.productDetailsElement && this.productFormElement) {

        // Add event listener for variantElement change
        this.variantElement && this.variantElement.addEventListener('change', this.variantChangeForYotpo.bind(this));

        // Run the Yotpo Subscription fix initially on page load.
        this.observeYotpoSubscriptionsWidget();
      }
    }
  }

  /**
   * Cleans up all event handlers that were assigned when the Product Form was constructed.
   * Useful for use when a section needs to be reloaded in the theme editor.
   */
  destroy() {
    this._listeners.removeAll();
  }

  /**
   * Getter method which returns the array of currently selected option values
   *
   * @returns {Array} An array of option values
   */
  options() {
    return this._serializeInputValues(this.optionInputs, function (item) {
      var regex = /(?:^(options\[))(.*?)(?:\])/;
      item.name = regex.exec(item.name)[2]; // Use just the value between 'options[' and ']'
      return item;
    });
  }

  /**
   * Getter method which returns the currently selected variant, or `null` if variant
   * doesn't exist.
   *
   * @returns {Object|null} Variant object
   */
  variant() {
    const opts = this.options();
    if (opts.length) {
      return getVariantFromSerializedArray(this.product, opts);
    } else {
      return this.product.variants[0];
    }
  }

  /**
   * Getter method which returns the current selling plan, or `null` if plan
   * doesn't exist.
   *
   * @returns {Object|null} Variant object
   */
  plan(variant) {
    let plan = {
      allocation: null,
      group: null,
      detail: null,
    };
    const formData = new FormData(this.form);
    const id = formData.get('selling_plan');

    if (id && variant) {
      plan.allocation = variant.selling_plan_allocations.find(function (item) {
        return item.selling_plan_id.toString() === id.toString();
      });
    }
    if (plan.allocation) {
      plan.group = this.product.selling_plan_groups.find(function (item) {
        return item.id.toString() === plan.allocation.selling_plan_group_id.toString();
      });
    }
    if (plan.group) {
      plan.detail = plan.group.selling_plans.find(function (item) {
        return item.id.toString() === id.toString();
      });
    }

    if (plan && plan.allocation && plan.detail && plan.allocation) {
      return plan;
    } else return null;
  }

  /**
   * Getter method which returns a collection of objects containing name and values
   * of property inputs
   *
   * @returns {Array} Collection of objects with name and value keys
   */
  properties() {
    return this._serializeInputValues(this.propertyInputs, function (item) {
      var regex = /(?:^(properties\[))(.*?)(?:\])/;
      item.name = regex.exec(item.name)[2]; // Use just the value between 'properties[' and ']'
      return item;
    });
  }

  /**
   * Getter method which returns the current quantity or 1 if no quantity input is
   * included in the form
   *
   * @returns {Array} Collection of objects with name and value keys
   */
  quantity() {
    return this.quantityInputs[0] ? Number.parseInt(this.quantityInputs[0].value, 10) : 1;
  }

  getFormState() {
    const variant = this.variant();
    return {
      options: this.options(),
      variant: variant,
      properties: this.properties(),
      quantity: this.quantity(),
      plan: this.plan(variant),
    };
  }

  // Private Methods
  // -----------------------------------------------------------------------------
  _setIdInputValue(variant) {
    if (variant && variant.id) {
      this.variantElement.value = variant.id.toString();
    } else {
      this.variantElement.value = '';
    }

    this.variantElement.dispatchEvent(new Event('change'));
  }

  _onSubmit(options, event) {
    event.dataset = this.getFormState();
    if (options.onFormSubmit) {
      options.onFormSubmit(event);
    }
  }

  _onOptionChange(event) {
    this._setIdInputValue(event.dataset.variant);
  }

  _onFormEvent(cb) {
    if (typeof cb === 'undefined') {
      return Function.prototype.bind();
    }

    return function (event) {
      event.dataset = this.getFormState();
      this._setIdInputValue(event.dataset.variant);
      cb(event);
    }.bind(this);
  }

  _initInputs(selector, cb) {
    var elements = Array.prototype.slice.call(this.element.querySelectorAll(selector));

    return elements.map(
      function (element) {
        this._listeners.add(element, 'change', this._onFormEvent(cb));
        return element;
      }.bind(this)
    );
  }

  _serializeInputValues(inputs, transform) {
    return inputs.reduce(function (options, input) {
      if (
        input.checked || // If input is a checked (means type radio or checkbox)
        (input.type !== 'radio' && input.type !== 'checkbox') // Or if its any other type of input
      ) {
        options.push(transform({ name: input.name, value: input.value }));
      }

      return options;
    }, []);
  }

  _validateProductObject(product) {
    if (typeof product !== 'object') {
      throw new TypeError(product + ' is not an object.');
    }

    if (typeof product.variants[0].options === 'undefined') {
      throw new TypeError(
        'Product object is invalid. Make sure you use the product object that is output from {{ product | json }} or from the http://[your-product-url].js route'
      );
    }
    return product;
  }

  isSubscriptionProduct = () => {
    return this.product.selling_plan_groups && this.product.selling_plan_groups.length > 0;
  };

  variantChangeForYotpo = () => {
    // Re-run the Yotpo Subscription fix when a variant option changes
    this.observeYotpoSubscriptionsWidget();
  }

  observeYotpoSubscriptionsWidget = () => {

    const observer = new MutationObserver((mutationList, observer) => {

      const yotpoWidgetContainer = this.productDetailsElement.querySelector('.yotpo-widget-subscriptions-add-to-cart');
      if (yotpoWidgetContainer && !yotpoWidgetContainer.hasAttribute('yp-input-target-updated')) {

        const productFormId = this.productFormElement.getAttribute('id');
        const yotpoInputs = yotpoWidgetContainer.querySelectorAll('.yotpo-selling-plan-radio-label');

        if (yotpoInputs.length > 0) {
          yotpoInputs.forEach(input => {
            if (input.getAttribute('form') !== productFormId) {
              input.setAttribute('form', productFormId);
            }
          });

          yotpoWidgetContainer.setAttribute('yp-input-target-updated', 'true');
          observer.disconnect();
        }
      }
    });

    // Start observing mutations within .product-single__details
    observer.observe(this.productDetailsElement, { childList: true, subtree: true });
  }
}

/**
   * Find a match in the project JSON (using Array with option values) and return the variant (as an Object)
   * @param {Object} product Product JSON object
   * @param {Array} options List of submitted values (e.g. ['36', 'Black'])
   * @returns {Object || null} The variant object once a match has been successful. Otherwise null will be returned
   */
function getVariantFromOptionArray(product, options) {
  _validateProductStructure(product);
  _validateOptionsArray(options);

  var result = product.variants.filter(function (variant) {
    return options.every(function (option, index) {
      return variant.options[index] === option;
    });
  });

  return result[0] || null;
}


/**
 * Convert the Object (with 'name' and 'value' keys) into an Array of values, then find a match & return the variant (as an Object)
 * @param {Object} product Product JSON object
 * @param {Object} collection Object with 'name' and 'value' keys (e.g. [{ name: "Size", value: "36" }, { name: "Color", value: "Black" }])
 * @returns {Object || null} The variant object once a match has been successful. Otherwise null will be returned
 */
export function getVariantFromSerializedArray(product, collection) {
  _validateProductStructure(product);

  // If value is an array of options
  var optionArray = _createOptionArrayFromOptionCollection(product, collection);
  return getVariantFromOptionArray(product, optionArray);
}

/**
   * Creates an array of selected options from the object
   * Loops through the project.options and check if the "option name" exist (product.options.name) and matches the target
   * @param {Object} product Product JSON object
   * @param {Array} collection Array of object (e.g. [{ name: "Size", value: "36" }, { name: "Color", value: "Black" }])
   * @returns {Array} The result of the matched values. (e.g. ['36', 'Black'])
   */
function _createOptionArrayFromOptionCollection(product, collection) {
  _validateProductStructure(product);
  _validateSerializedArray(collection);

  var optionArray = [];

  collection.forEach(function (option) {
    for (var i = 0; i < product.options.length; i++) {
      var name = product.options[i].name || product.options[i];
      if (name.toLowerCase() === option.name.toLowerCase()) {
        optionArray[i] = option.value;
        break;
      }
    }
  });

  return optionArray;
}

/**
   * Check if the product data is a valid JS object
   * Error will be thrown if type is invalid
   * @param {object} product Product JSON object
   */
function _validateProductStructure(product) {
  if (typeof product !== 'object') {
    throw new TypeError(product + ' is not an object.');
  }

  if (Object.keys(product).length === 0 && product.constructor === Object) {
    throw new Error(product + ' is empty.');
  }
}

/**
 * Validate the structure of the array
 * It must be formatted like jQuery's serializeArray()
 * @param {Array} collection Array of object [{ name: "Size", value: "36" }, { name: "Color", value: "Black" }]
 */
function _validateSerializedArray(collection) {
  if (!Array.isArray(collection)) {
    throw new TypeError(collection + ' is not an array.');
  }

  if (collection.length === 0) {
    throw new Error(collection + ' is empty.');
  }

  if (collection[0].hasOwnProperty('name')) {
    if (typeof collection[0].name !== 'string') {
      throw new TypeError(
        'Invalid value type passed for name of option ' + collection[0].name + '. Value should be string.'
      );
    }
  } else {
    throw new Error(collection[0] + 'does not contain name key.');
  }
}

/**
 * Validate the structure of the array
 * It must be formatted as list of values
 * @param {Array} collection Array of object (e.g. ['36', 'Black'])
 */
function _validateOptionsArray(options) {
  if (Array.isArray(options) && typeof options[0] === 'object') {
    throw new Error(options + 'is not a valid array of options.');
  }
}